import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/Card';
import { formatValue, formatRange } from './format';
import apiClient from '../../utils/api';

// Colour the index by band: optimal (≥80) green, decent (≥60) amber, else gray.
const indexTone = (v) => {
  if (v == null) return 'text-gray-400';
  if (v >= 80) return 'text-green-600 dark:text-green-400';
  if (v >= 60) return 'text-amber-600 dark:text-amber-400';
  return 'text-gray-600 dark:text-gray-300';
};

const barTone = (sub) => {
  if (sub >= 80) return 'bg-green-500';
  if (sub >= 50) return 'bg-amber-500';
  return 'bg-gray-400';
};

// Is a driver's latest value already inside its optimal band?
const driverInBand = (d) => {
  if (d.value == null) return null;
  const { direction, optimal_low: lo, optimal_high: hi } = d;
  if (direction === 'up') return lo != null && d.value >= lo;
  if (direction === 'down') return hi != null && d.value <= hi;
  return lo != null && hi != null && d.value >= lo && d.value <= hi;
};

const MitoIndexCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWhy, setShowWhy] = useState(false);

  useEffect(() => {
    let alive = true;
    apiClient
      .get('/api/twin/mito')
      .then((res) => alive && setData(res.data))
      .catch(() => alive && setData(false))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
        </CardContent>
      </Card>
    );
  }
  if (!data) return null; // failed silently — don't block the rest of the twin

  const { index, coverage, components, missing, drivers } = data;
  const pctCoverage = coverage != null ? Math.round(coverage * 100) : 0;
  const present = components.length;
  const total = present + (missing?.length || 0);

  return (
    <Card>
      <CardContent className="py-5">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              Mitochondrial function
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-0.5">
              A proxy composite — how well your cells make energy
            </p>
          </div>
          <div className="shrink-0 sm:text-right">
            {index != null ? (
              <>
                <span className={`text-4xl font-extrabold tabular-nums ${indexTone(index)}`}>
                  {Math.round(index)}
                </span>
                <span className="text-sm text-gray-600 dark:text-gray-300">/100</span>
              </>
            ) : (
              <span className="text-sm text-gray-600 dark:text-gray-300">Gathering readouts</span>
            )}
          </div>
        </div>

        {/* Coverage — honest about how much of the signal we actually have */}
        <div className="mt-3">
          <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-300 mb-1">
            <span>
              Based on {present} of {total} readouts
            </span>
            <span>{pctCoverage}% of the signal</span>
          </div>
          <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
            <div
              className="h-full bg-primary-500 rounded-full transition-all"
              style={{ width: `${pctCoverage}%` }}
            />
          </div>
          {missing && missing.length > 0 && (
            <p className="mt-1.5 text-sm text-gray-600 dark:text-gray-300">
              Connect Fitbit cardio fitness & HRV to complete the picture
              {index != null && ' — the number sharpens as more readouts come in'}.
            </p>
          )}
        </div>

        {/* Component breakdown — every input, its subscore, its weight */}
        {components.length > 0 && (
          <div className="mt-4 space-y-2.5">
            {components.map((c) => (
              <div key={c.metric_key}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-700 dark:text-gray-200">{c.label}</span>
                  <span className="text-gray-600 dark:text-gray-300 tabular-nums">
                    {Math.round(c.subscore)}/100
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      {' '}· {Math.round(c.weight * 100)}% weight
                    </span>
                  </span>
                </div>
                <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${barTone(c.subscore)}`}
                    style={{ width: `${Math.max(2, Math.round(c.subscore))}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Drivers — the levers, deliberately outside the score */}
        {drivers && drivers.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700/60">
            <p className="text-sm font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300 mb-2">
              Levers you can pull
            </p>
            <div className="flex flex-wrap gap-2">
              {drivers.map((d) => {
                const inBand = driverInBand(d);
                return (
                  <div
                    key={d.metric_key}
                    className="flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-700 px-2.5 py-1.5 text-sm"
                  >
                    <span className="text-gray-600 dark:text-gray-300">{d.label}</span>
                    <span className="font-semibold text-gray-900 dark:text-white tabular-nums">
                      {formatValue(d.value, d.unit)}
                    </span>
                    {inBand === true && <span className="text-green-600 dark:text-green-400">✓</span>}
                    {inBand === false && d.optimal_low != null && (
                      <span className="text-gray-600 dark:text-gray-300">
                        → {formatRange(d.optimal_low, d.optimal_high, d.unit)}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              Sleep and vigorous activity build mitochondria over time — they move the
              readouts above, not the score directly.
            </p>
          </div>
        )}

        {/* Why-this-number transparency + wellness guardrail */}
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setShowWhy((s) => !s)}
            className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
          >
            {showWhy ? 'Hide' : 'What is this?'}
          </button>
          {showWhy && (
            <p className="mt-1.5 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
              {data.disclaimer}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default MitoIndexCard;
